import { Dialog, PlainButton, TextInput } from "@jugl-web/ui-components";
import React, { useEffect, useMemo, useState } from "react";
import { cx, useTranslations, useToast } from "@jugl-web/utils";
import { CountryPicker } from "@jugl-web/ui-components/web";
import { CountryPickerValue } from "@jugl-web/ui-components/web/CountryPicker/types";
import { isValidEmail } from "@jugl-web/utils/validators";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { MAX_WORKSPACE_NAME_LENGTH } from "@web-src/modules/auth/conts";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { Button } from "@jugl-web/ui-kit";
import { useDefaultCountry } from "../../hooks/useDefaultCountry";
import { ReactComponent as CrossIcon } from "../CXSignupDialog/assets/cross.svg";
import instagramShadowImage from "../CXSignupDialog/assets/instagram-shadow.png";
import instagramGhostImage from "../CXSignupDialog/assets/instagram-ghost.png";
import whatsappShadowImage from "../CXSignupDialog/assets/whatsapp-shadow.png";
import whatsappGhostImage from "../CXSignupDialog/assets/whatsapp-ghost.png";
import webShadowImage from "../CXSignupDialog/assets/web-shadow.png";
import webGhostImage from "../CXSignupDialog/assets/web-ghost.png";
import twilioShadowImage from "../CXSignupDialog/assets/twilio-shadow.png";
import twilioGhostImage from "../CXSignupDialog/assets/twilio-ghost.png";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";

const slides = [
  { ghost: instagramGhostImage, shadow: instagramShadowImage },
  { ghost: whatsappGhostImage, shadow: whatsappShadowImage },
  { ghost: webGhostImage, shadow: webShadowImage },
  { ghost: twilioGhostImage, shadow: twilioShadowImage },
];

export const CreateCXWorkspaceDialog: React.FC<{
  isOpen: boolean;
  onRequestClose: () => void;
}> = ({ isOpen, onRequestClose }) => {
  const { t } = useTranslations();
  const { toast } = useToast({ variant: "web" });
  const { entitiesApi } = useRestApiProvider();
  const { generateUrl } = useNavigation();
  const [createEntity, { isLoading }] = entitiesApi.useCreateEntityMutation();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [workspaceName, setWorkspaceName] = useState("");
  const [workspaceCountry, setWorkspaceCountry] =
    useState<CountryPickerValue>();
  const [workspaceEmail, setWorkspaceEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const defaultCountry = useDefaultCountry();

  useEffect(() => {
    if (defaultCountry && !workspaceCountry) {
      setWorkspaceCountry(defaultCountry);
    }
  }, [defaultCountry, workspaceCountry]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const interval = setInterval(() => {
      setCurrentIndex((c) => (c + 1) % slides.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [isOpen]);

  const handleClose = () => {
    onRequestClose();
    setWorkspaceName("");
    setWorkspaceCountry(undefined);
    setWorkspaceEmail("");
    setIsSubmitted(false);
  };

  const workspaceEmailError = useMemo(() => {
    if (!isSubmitted) return undefined;
    if (!workspaceEmail.length) {
      return t({
        id: "form-controls.email.error-is-required",
        defaultMessage: "Email is empty",
      });
    }
    if (!isValidEmail(workspaceEmail)) {
      return t({
        id: "form-controls.email.error-is-incorrect",
        defaultMessage: "Email is incorrect",
      });
    }
    return undefined;
  }, [isSubmitted, workspaceEmail, t]);

  const allFieldsHaveValues =
    !!workspaceName.trim() && !!workspaceCountry?.iso && !!workspaceEmail;

  const handleSubmit = async () => {
    setIsSubmitted(true);
    if (
      !workspaceName.trim() ||
      !workspaceCountry?.iso ||
      !isValidEmail(workspaceEmail)
    ) {
      return;
    }

    try {
      const result = await createEntity({
        type: "cx",
        data: {
          name: workspaceName.trim(),
          country: workspaceCountry.iso,
          billing_email: workspaceEmail ?? undefined,
        },
      }).unwrap();

      handleClose();
      window.location.href = generateUrl("cxAIAgents", undefined, {
        entityId: result.id,
      });
    } catch {
      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong.",
        }),
        { variant: "error" }
      );
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      className="fixed flex h-full max-h-[min(783px,95vh)] w-full max-w-[min(1310px,95%)] items-center overflow-hidden rounded-2xl bg-white"
    >
      <PlainButton
        className="absolute top-5 right-5 z-10 flex h-8 w-8 items-center justify-center"
        onClick={handleClose}
      >
        <CrossIcon />
      </PlainButton>
      <div className="bg-dark relative mr-12 hidden h-full w-[476px] shrink-0 [@media(min-width:1170px)_and_(min-height:650px)]:block">
        {slides.map((slide, index) => (
          <div
            key={+index}
            className={cx(
              "pointer-events-none transition-opacity duration-700",
              index === currentIndex ? "opacity-100" : "opacity-0"
            )}
          >
            <img
              src={slide.shadow}
              className="absolute left-0 top-1/2 w-3/4 -translate-y-1/2"
              alt=""
            />
            <img
              src={slide.ghost}
              className="absolute left-0 top-1/2 w-[510px] -translate-y-1/2"
              alt=""
            />
          </div>
        ))}
      </div>
      <div className="jugl__custom-scrollbar flex h-full w-full flex-col items-center justify-center gap-8 overflow-y-auto py-4 px-10">
        <div className="my-auto flex w-[500px] flex-col items-center justify-center py-4">
          <span className="text-dark font-secondary text-[32px] font-medium leading-[48px]">
            {t({
              id: "cx-ai-agents-page.lets-start-with-essentials",
              defaultMessage: "Let's start with essentials",
            })}
          </span>
          <span className="text-grey-900 font-secondary mt-2 text-sm leading-4">
            {t({
              id: "cx-ai-agents-page.just-a-few-details",
              defaultMessage: "Just a few details to start with",
            })}
          </span>
          <div className="mt-10 flex w-full flex-col gap-6">
            <div className="flex gap-4">
              <div className="min-w-0 flex-[6_6_0%]">
                <TextInput
                  placeholder={t({
                    id: "common.enter",
                    defaultMessage: "Enter",
                  })}
                  label={t({
                    id: "create-entity-form.workspace-name",
                    defaultMessage: "Workspace Name",
                  })}
                  isRequired
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                  isInvalid={isSubmitted && !workspaceName.trim()}
                  classNames={{ wrapperClassName: "w-full" }}
                  maxLength={MAX_WORKSPACE_NAME_LENGTH}
                />
              </div>
              <div className="min-w-0 flex-[4_4_0%]">
                <CountryPicker
                  countryIso={workspaceCountry?.iso}
                  onSelect={(item) => setWorkspaceCountry(item)}
                  placement="top"
                  className="z-[9999]"
                >
                  {() => (
                    <TextInput
                      value={
                        workspaceCountry?.name
                          ? `${workspaceCountry.name} (${workspaceCountry.iso})`
                          : ""
                      }
                      isRequired
                      label={t({
                        id: "create-entity-form.workspace-country",
                        defaultMessage: "Workspace Country",
                      })}
                      placeholder={t({
                        id: "common.select",
                        defaultMessage: "Select",
                      })}
                      classNames={{
                        wrapperClassName: "w-full",
                        inputClassName: "cursor-pointer",
                      }}
                      isInvalid={isSubmitted && !workspaceCountry?.iso}
                      readOnly
                    />
                  )}
                </CountryPicker>
              </div>
            </div>
            <TextInput
              placeholder="email@gmail.com"
              label={t({
                id: "create-entity-form.workspace-email",
                defaultMessage: "Workspace Email",
              })}
              isRequired
              value={workspaceEmail}
              onChange={(e) => setWorkspaceEmail(e.target.value)}
              isInvalid={isSubmitted && !!workspaceEmailError}
              errorMessage={workspaceEmailError}
              type="email"
            />
          </div>
          <Button
            className="mt-10 w-[500px]"
            onClick={handleSubmit}
            isDisabled={!allFieldsHaveValues || isLoading}
            iconEnd={<ArrowIcon />}
            isCx
          >
            {t({
              id: "cx-ai-agents-page.create-workspace",
              defaultMessage: "Create Workspace",
            })}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
